import { Box } from "@mantine/core";
import { Map, ViewState } from 'react-map-gl';
import { useEffect, useRef, useState, forwardRef } from "react";
import mapboxgl from "mapbox-gl";
import 'mapbox-gl/dist/mapbox-gl.css';

import { MapRoutePinsSourceAndLayers, MapRouteSourceAndLayers, SelectedPinOverlay } from "./MapSource";
import { Pin } from "Modules/Map/MapComponent";

const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN;

export interface MapCanvasHandle {
  getViewBoxPins: () => Pin[] | null;
};

interface Props {
  onMapLoaded: () => void;
  viewState: ViewState;
  routeData?: number[][];
  routePins?: {
    id: number;
    latLon: [number, number];
  }[];
  selectedPinIndex?: number;
  onMove: (viewState: ViewState) => void;
}
const MapCanvas = forwardRef<MapCanvasHandle, Props>(({
  onMapLoaded,
  viewState,
  routeData,
  routePins,
  selectedPinIndex,
  onMove,
}, ref) => {
  const mapRef = useRef<mapboxgl.Map | undefined>();
  const [isMapReady, setIsMapReady] = useState(false);
  const [routeGeoJSON, setRouteGeoJSON] = useState<GeoJSON.Feature<GeoJSON.LineString>
   | null>(null);
  const [routePinsGeoJSON, setRoutePinsGeoJSON] = useState<GeoJSON.FeatureCollection<GeoJSON.Point,
    { id: number; index: number; }>>();
  const [touchStart, setTouchStart] = useState<{ x: number; y: number; time: number } | null>(null);

  useEffect(() => {
    // COMPLETE ZOOM PREVENTION: Disable ALL browser zoom while map view is active
    console.log('Map mounted: Disabling all browser zoom');

    document.body.style.overscrollBehavior = 'none';
    if (mapRef.current) {
      mapRef.current.getCanvas().style.touchAction = 'none';
    }

    // 1. Prevent keyboard zoom (Cmd/Ctrl + Plus/Minus/0)
    const preventKeyboardZoom = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '-' || e.key === '0' || e.key === '=')) {
        e.preventDefault();
      }
    };

    // 2. Prevent ALL trackpad/mouse wheel zoom globally
    const preventWheelZoom = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
      }
    };

    // 3. Prevent gesture zoom (Safari)
    const preventGestureZoom = (e: Event) => {
      e.preventDefault();
    };

    // Add global listeners
    document.addEventListener('keydown', preventKeyboardZoom);
    document.addEventListener('wheel', preventWheelZoom, { passive: false });
    document.addEventListener('gesturestart', preventGestureZoom);
    document.addEventListener('gesturechange', preventGestureZoom);
    document.addEventListener('gestureend', preventGestureZoom);

    // Cleanup on unmount - restore normal zoom behavior
    return () => {
      console.log('Map unmounted: Re-enabling browser zoom');
      document.removeEventListener('keydown', preventKeyboardZoom);
      document.removeEventListener('wheel', preventWheelZoom);
      document.removeEventListener('gesturestart', preventGestureZoom);
      document.removeEventListener('gesturechange', preventGestureZoom);
      document.removeEventListener('gestureend', preventGestureZoom);
      document.body.style.overscrollBehavior = 'auto';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Additional touch handling for map interactions (simplified since zoom is globally disabled)
  useEffect(() => {
    // Prevent iOS zoom on double-tap
    let lastTouchEnd = 0;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let touchStartTime = 0;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let initialTouchDistance = 0;

    const preventZoom = (e: TouchEvent) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        e.preventDefault();
        e.stopPropagation();
      }
      lastTouchEnd = now;
    };

    // Enhanced pinch zoom prevention that tracks gesture intent
    const handleTouchStart = (e: TouchEvent) => {
      touchStartTime = Date.now();

      // If multi-touch (pinch gesture starting)
      if (e.touches.length === 2) {
        // Check if ANY touch is near the map (even if one is outside)
        const mapRect = mapRef.current?.getContainer()?.getBoundingClientRect();
        if (mapRect) {
          const expandedBounds = 100; // More generous for touch

          // Check if either touch point is near the map
          const touch1Near = (
            e.touches[0].clientX >= (mapRect.left - expandedBounds) &&
            e.touches[0].clientX <= (mapRect.right + expandedBounds) &&
            e.touches[0].clientY >= (mapRect.top - expandedBounds) &&
            e.touches[0].clientY <= (mapRect.bottom + expandedBounds)
          );

          const touch2Near = (
            e.touches[1].clientX >= (mapRect.left - expandedBounds) &&
            e.touches[1].clientX <= (mapRect.right + expandedBounds) &&
            e.touches[1].clientY >= (mapRect.top - expandedBounds) &&
            e.touches[1].clientY <= (mapRect.bottom + expandedBounds)
          );

          // If either touch is near map, prevent browser zoom
          if (touch1Near || touch2Near) {
            e.preventDefault();
          }
        } else {
          // Fallback: always prevent on multi-touch
          e.preventDefault();
        }

        // Calculate initial distance between touches
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        initialTouchDistance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        );
      }
      // Single touch - might be start of tap or pan
      else if (e.touches.length === 1) {
        // Don't prevent default yet - let map handle panning
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      // For pinch gestures, always prevent viewport zoom
      if (e.touches.length === 2) {
        e.preventDefault();

        // Let Mapbox handle the actual map zoom
        // but prevent iOS from zooming the viewport
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const currentDistance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        );

        // Optional: Add custom zoom handling if needed
        // const zoomDelta = currentDistance - initialTouchDistance;
      }
    };

    // Chrome desktop: Prevent trackpad pinch zoom
    const handleWheel = (e: WheelEvent) => {
      // Chrome fires wheel events with ctrlKey=true for pinch gestures on trackpad
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        // The map will still zoom via Mapbox's own handlers
        // but we prevent the browser viewport zoom
      }
    };

    const mapContainer = mapRef.current?.getContainer();
    if (mapContainer) {
      // Add touch event listeners with passive: false to allow preventDefault
      mapContainer.addEventListener('touchend', preventZoom, { passive: false });
      mapContainer.addEventListener('touchstart', handleTouchStart, { passive: false });
      mapContainer.addEventListener('touchmove', handleTouchMove, { passive: false });

      // Chrome desktop: Prevent trackpad pinch from zooming browser
      mapContainer.addEventListener('wheel', handleWheel, { passive: false });

      // Gesture events for Safari - these are critical for preventing viewport zoom
      const preventGesture = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
      };

      // These events are Safari-specific and fire for pinch gestures
      document.addEventListener('gesturestart', preventGesture, { passive: false });
      document.addEventListener('gesturechange', preventGesture, { passive: false });
      document.addEventListener('gestureend', preventGesture, { passive: false });

      // Note: Document-level wheel zoom is now handled globally in the first useEffect
      // This local handler is kept for potential future use but currently redundant

      return () => {
        mapContainer.removeEventListener('touchend', preventZoom);
        mapContainer.removeEventListener('touchstart', handleTouchStart);
        mapContainer.removeEventListener('touchmove', handleTouchMove);
        mapContainer.removeEventListener('wheel', handleWheel);
        document.removeEventListener('gesturestart', preventGesture);
        document.removeEventListener('gesturechange', preventGesture);
        document.removeEventListener('gestureend', preventGesture);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef.current]);

  useEffect(() => {
    if (routeData) {
      const g: GeoJSON.Feature<GeoJSON.LineString> = {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: routeData,
        }
      };
      setRouteGeoJSON(g);
    } else {
      setRouteGeoJSON(null);
    }
  }, [routeData]);

  // NOTE (EIS-1089): an inert, permanently hidden GeolocateControl used to be
  // mounted here. It was never triggered, and its
  // `document.querySelector('.mapboxgl-ctrl-geolocate')` was a GLOBAL query that
  // could hide the other map canvas's control. Removed.

  useEffect(() => {
    if (routePins) {
      const features: GeoJSON.Feature<GeoJSON.Point, { id: number, index: number }>[] = [];
      routePins
        .forEach((p, index) => {
          const lon = p.latLon[1];
          const lat = p.latLon[0];
          features.push({
            type: "Feature",
            properties: {
              id: p.id,
              index,
            },
            geometry: {
              type: 'Point',
              coordinates: [lon, lat]
            }
          })
        });

      const ds: GeoJSON.FeatureCollection<GeoJSON.Point, { id: number, index: number }> = {
        type: "FeatureCollection",
        features
      }
      console.log('Setting route pins geojson:', ds);
      setRoutePinsGeoJSON(ds);
    } else {
      setRoutePinsGeoJSON(undefined);
    }
  }, [routePins]);

  return (
    <Box style={{zIndex: 1000}}>
      <Map
        ref={mapRef as any}
        {...viewState}
        style={{
          position: 'absolute',
          width: '100vw',
          height: '100%',
          top: '0',
          left: '0'
        }}
        touchPitch={false}
        dragRotate={false}
        touchZoomRotate={{
          disableRotation: true
        }}
        onTouchStart={(e) => {
          setTouchStart({
            x: e.point.x,
            y: e.point.y,
            time: Date.now()
          });
        }}
        onTouchEnd={(e) => {
          if (!touchStart) return;

          const touchEnd = {
            x: e.point.x,
            y: e.point.y,
            time: Date.now()
          };

          const distance = Math.sqrt(
            Math.pow(touchEnd.x - touchStart.x, 2) +
            Math.pow(touchEnd.y - touchStart.y, 2)
          );
          const duration = touchEnd.time - touchStart.time;

          if (duration < 300 && distance < 10) {
            // Guard against race condition when map tiles aren't fully loaded
            let featureEvent: mapboxgl.MapboxGeoJSONFeature | undefined;
            try {
              [featureEvent] = e.target.queryRenderedFeatures(e.point);
            } catch (err) {
              console.warn('Map query failed - tiles may not be loaded:', err);
              return;
            }

            if (featureEvent && (featureEvent.layer?.id === "clusters" || featureEvent.layer?.id === "cluster-count")) {
              e.preventDefault();
              const clusterId = featureEvent.properties?.cluster_id;
              const coordinates = (featureEvent.geometry as GeoJSON.Point).coordinates;
              const source = mapRef.current?.getSource('practitioners-source') as mapboxgl.GeoJSONSource;

              if (source && clusterId !== undefined && mapRef.current) {
                source.getClusterExpansionZoom(clusterId, (err, zoom) => {
                  if (err || !mapRef.current) return;

                  const targetZoom = Math.max(12, (zoom || 10) + 1);

                  mapRef.current.flyTo({
                    center: [coordinates[0], coordinates[1]],
                    zoom: targetZoom,
                    speed: 2,
                    curve: 1.5,
                    bearing: 0,
                    pitch: 0,
                    essential: true,
                  });
                });
              }
              return;
            }
          }

          setTouchStart(null);
        }}
        onClick={(e) => {
          // Guard against race condition when map tiles aren't fully loaded
          let featureEvent: mapboxgl.MapboxGeoJSONFeature | undefined;
          try {
            [featureEvent] = e.target.queryRenderedFeatures(e.point);
          } catch (err) {
            console.warn('Map query failed - tiles may not be loaded:', err);
            return;
          }
          if (featureEvent && (featureEvent.layer?.id === "clusters" || featureEvent.layer?.id === "cluster-count")) {
            e.preventDefault();
            const clusterId = featureEvent.properties?.cluster_id;
            const coordinates = (featureEvent.geometry as GeoJSON.Point).coordinates;
            const source = mapRef.current?.getSource('practitioners-source') as mapboxgl.GeoJSONSource;

            if (source && clusterId !== undefined && mapRef.current) {
              source.getClusterExpansionZoom(clusterId, (err, zoom) => {
                if (err || !mapRef.current) return;

                const targetZoom = Math.max(12, (zoom || 10) + 1);

                mapRef.current.flyTo({
                  center: [coordinates[0], coordinates[1]],
                  zoom: targetZoom,
                  speed: 2,
                  curve: 1.5,
                  bearing: 0,
                  pitch: 0,
                  essential: true,
                });
              });
            }
            return;
          }
        }}
        onLoad={() => {
          mapRef?.current?.loadImage("/icons/route-marker-not-focused.png", (err, img) => {
            if (err || !img) throw err;
            mapRef?.current?.addImage('custom-marker-route', img);
          });
          mapRef?.current?.loadImage("/icons/selected-marker-green.png", (err, img) => {
            if (err || !img) throw err;
            mapRef?.current?.addImage('selected-marker-green', img);
          });
          onMapLoaded();
          setIsMapReady(true);
        }}
        onMove={(e) => onMove(e.viewState)}
        onData={(e: any) => {
          if (e?.sourceId === "practitioners-source") {
            const map = mapRef.current;
            const features = map?.querySourceFeatures("practitioners-source", {
              sourceLayer: "unclustered-points",
            });
            const idsToFetch: number[] = [];
            features?.forEach((f) => {
              if (!f?.properties?.point_count) {

                if (f?.properties?.id && !idsToFetch.includes(f?.properties?.id)) {
                  idsToFetch.push(f.properties.id);
                }
              }
            });
          }
        }}
        mapStyle="mapbox://styles/mapbox/standard"
        mapboxAccessToken={mapboxToken}
      >
        {isMapReady && routeGeoJSON && (
          <MapRouteSourceAndLayers
            id="route"
            type="geojson"
            data={routeGeoJSON} />
        )}
        {isMapReady && routePinsGeoJSON && (
          <MapRoutePinsSourceAndLayers
            id="route-source"
            type="geojson"
            data={routePinsGeoJSON}
          />
        )}

        {isMapReady && routePinsGeoJSON && (
          <SelectedPinOverlay
            id="selected-overlay"
            type="geojson"
            data={routePinsGeoJSON}
            selectedPinIndex={selectedPinIndex} />
        )}
      </Map>
    </Box >
  );
});

export default MapCanvas;